import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation, EventEmitter } from '@angular/core';
import videojs from 'video.js';
import Player from 'video.js/dist/types/player';
import { CameraService } from '../../../services/camera.service';
import { Camera, Device, StreamState } from '../models';

@Component({
  selector: 'stream-viewer',
  templateUrl: './stream-viewer.component.html',
  styleUrls: ['./stream-viewer.component.scss'],
  providers: [CameraService],
  encapsulation: ViewEncapsulation.None,
})
export class StreamViewerComponent implements OnChanges, OnInit, OnDestroy {

  options = {
    liveui: true,
    autoplay: true,
    controlBar: {
      pictureInPictureToggle: false
    },
    plugins: {
      reloadSourceOnError: {}
    }
  };

  @ViewChild('target', { static: true }) target: ElementRef;
  @Input() selectedDevice: Device;
  @Output() close = new EventEmitter();
  cameras: Camera[] = [];
  state: StreamState = StreamState.LoadingCameras;
  player: Player;
  timeout: any;

  _selectedCamera: Camera;
  set selectedCamera(value: Camera) {
    this._selectedCamera = value;
    this.log("Selecting camera " + JSON.stringify(value));
    if (value == null) {
      return;
    }
    this.loadStream(value);
  }

  get selectedCamera(): Camera {
    return this._selectedCamera;
  }

  constructor(
    private cameraService: CameraService) {
    this.state = StreamState.Idle;
  }

  // Initialise the player and hook error handling
  ngOnInit() {
    this.state = StreamState.LoadingPlayer;
    this.player = videojs(this.target?.nativeElement, this.options, () => { });
    this.player.on('error', () => this.handleError(this.player.error()));

    const playerTech = this.player.tech({ IWillNotUseThisInPlugins: true });
    playerTech.on('retryplaylist', (e) => { this.handleError("Couldn't load playlist"); });
  }

  // If the selected device has changed, load the cameras for the new device and start the stream
  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedDevice && changes.selectedDevice.previousValue != changes.selectedDevice.currentValue) {
      this.selectedDevice = changes.selectedDevice.currentValue;
      this.loadCameras(this.selectedDevice.deviceGroupUserId);
    }
  }

  // If the stream has been closed, clear state and emit the close event
  deselectDevice() {
    this.state = StreamState.Idle;
    this.selectedCamera = null;
    this.selectedDevice = null;
    this.close.emit(null);
  }

  // Load the cameras for the selected device
  loadCameras(device = this.selectedDevice?.deviceGroupUserId): PromiseLike<any> {

    if (device == null) {
      return;
    }

    this.state = StreamState.LoadingCameras;
    return this.cameraService.getCameras(device)
      .then((data) => {
        this.cameras = data;
        this.selectedCamera = data[0];
      })
      .catch(() => this.handleError("Failed to load cameras"));

  }

  // Get the stream URL for the selected camera and load it into the player
  loadStream(camera: Camera = this.selectedCamera): PromiseLike<any> {

    this.timeout = null;
    if (camera == null) {
      return;
    }

    return this.cameraService.getStream(camera)
      .then((stream) => {
        this.player.src(stream);
        this.player.play()
          .then(() => {
            this.timeout = null;
            this.state = StreamState.Streaming;
          })
          .catch(() => this.handleError("Error connecting to media"));
      })
      .catch(() => this.handleError("Error getting stream URL"));
  }

  log($event: any) {
    console.log($event);
  }

  handleError(error = null) {

    console.error("Camera Stream: " + error);

    this.state = StreamState.Error;
    if (this.timeout) {
      return;
    }

    this.timeout = setTimeout(() => this.loadStream(), 1000);

  }

  // Dispose the player OnDestroy
  ngOnDestroy() {
    if (this.player) {
      this.player.dispose();
    }
  }

}

